import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, X } from "lucide-react";
import { getMotiveIcon, scoreBand } from "@/lib/motives";
import {
  Radar,
  RadarChart as RC,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = ["#CCFF00", "#00F0FF", "#EC4899"];

export default function TeamCompare() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [motives, setMotives] = useState([]);
  const [selected, setSelected] = useState([]); // player ids
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, ps, m] = await Promise.all([
          api.get(`/teams/${teamId}`),
          api.get(`/teams/${teamId}/players`),
          api.get("/meta/motives"),
        ]);
        setTeam(t.data);
        setAllPlayers(ps.data.players.filter((p) => p.test_status === "completed"));
        setMotives(m.data.motives);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [teamId]);

  const toggle = (id) => {
    setData(null);
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 3) {
        toast.error("Maximal 3 Spieler gleichzeitig vergleichen.");
        return cur;
      }
      return [...cur, id];
    });
  };

  const runCompare = async () => {
    if (selected.length < 2) {
      toast.error("Bitte mindestens 2 Spieler auswählen.");
      return;
    }
    setComparing(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/compare`, {
        params: { player_ids: selected.join(",") },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setComparing(false);
    }
  };

  // Chart data
  const chartData =
    data && motives.length
      ? motives.map((m) => {
          const row = { motive: m.short, fullName: m.name };
          data.players.forEach((p) => {
            row[p.name] = p.scores[m.slug] || 0;
          });
          return row;
        })
      : [];

  if (loading)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center text-white/40">
        Wird geladen …
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 space-y-6">
      <Link
        to={`/teams/${teamId}`}
        data-testid="compare-back-btn"
        className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#CCFF00] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zum Team
      </Link>

      <div>
        <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
          / VERGLEICH · {team?.name}
        </div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight mb-2">
          Bis zu 3 Spieler vergleichen
        </h1>
        <p className="text-white/60">
          Wähle 2-3 Spieler mit abgeschlossenem Test aus. Ihre Motive werden im Radar überlagert dargestellt.
        </p>
      </div>

      {allPlayers.length < 2 ? (
        <div className="border border-white/10 border-dashed rounded-md p-10 text-center">
          <p className="text-white/60">
            Für einen Vergleich müssen mindestens 2 Spieler ihren Test abgeschlossen haben.
          </p>
        </div>
      ) : (
        <>
          {/* Auswahl */}
          <div className="card-elevated p-6">
            <div className="font-display text-xs tracking-[0.3em] text-white/40 mb-4">
              / AUSWAHL ({selected.length}/3)
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
              {allPlayers.map((p) => {
                const isSel = selected.includes(p.id);
                const color = isSel ? COLORS[selected.indexOf(p.id)] : null;
                return (
                  <button
                    key={p.id}
                    data-testid={`compare-toggle-${p.id}`}
                    onClick={() => toggle(p.id)}
                    className={`text-left p-3 border rounded-md transition-all ${
                      isSel
                        ? "bg-white/5"
                        : "border-white/10 hover:border-white/20 bg-[#0f1013]"
                    }`}
                    style={
                      isSel ? { borderColor: color, boxShadow: `0 0 0 1px ${color}44` } : {}
                    }
                  >
                    <div className="flex items-center gap-2">
                      {isSel && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ background: color }}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-sm truncate">
                          {p.name}
                        </div>
                        <div className="text-xs text-white/50 truncate">
                          {p.position || "-"}
                          {p.jersey_number ? ` · #${p.jersey_number}` : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-5">
              <button
                data-testid="compare-run-btn"
                onClick={runCompare}
                disabled={selected.length < 2 || comparing}
                className="btn-primary disabled:opacity-40"
              >
                {comparing ? "Vergleich läuft …" : "Vergleich starten"}
              </button>
            </div>
          </div>

          {/* Ergebnis */}
          {data && (
            <>
              <div className="card-elevated p-6" data-testid="compare-result">
                <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
                  / RADAR-VERGLEICH
                </div>
                <h3 className="font-heading font-black text-2xl mb-4">
                  Motive im direkten Vergleich
                </h3>
                <div style={{ width: "100%", height: 460 }}>
                  <ResponsiveContainer>
                    <RC data={chartData} outerRadius="70%">
                      <PolarGrid stroke="rgba(255,255,255,0.1)" />
                      <PolarAngleAxis
                        dataKey="motive"
                        tick={{
                          fill: "#ffffff",
                          fontSize: 11,
                          fontFamily: "Barlow, sans-serif",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        tick={{ fill: "#64748B", fontSize: 9 }}
                        stroke="rgba(255,255,255,0.1)"
                      />
                      {data.players.map((p, i) => (
                        <Radar
                          key={p.id}
                          name={p.name}
                          dataKey={p.name}
                          stroke={COLORS[i]}
                          fill={COLORS[i]}
                          fillOpacity={0.15}
                          strokeWidth={2}
                        />
                      ))}
                      <Tooltip
                        contentStyle={{
                          background: "#111214",
                          border: "1px solid rgba(255,255,255,0.15)",
                          borderRadius: 6,
                        }}
                      />
                      <Legend
                        wrapperStyle={{
                          fontFamily: "Barlow, sans-serif",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: 12,
                          letterSpacing: "0.1em",
                        }}
                      />
                    </RC>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Spielertyp-Karten */}
              <div className="grid md:grid-cols-3 gap-4">
                {data.players.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`compare-card-${p.id}`}
                    className="p-6 border rounded-md bg-[#0f1013] relative overflow-hidden"
                    style={{ borderColor: `${COLORS[i]}66` }}
                  >
                    <div
                      className="absolute top-0 left-0 h-full w-1"
                      style={{ background: COLORS[i] }}
                    />
                    <div className="font-heading font-bold text-lg">{p.name}</div>
                    <div className="text-xs text-white/50 mb-4">
                      {p.position || "-"}
                      {p.jersey_number ? ` · #${p.jersey_number}` : ""}
                    </div>
                    <div
                      className="font-heading font-black text-xl leading-tight mb-1"
                      style={{ color: COLORS[i] }}
                    >
                      {p.archetype.name}
                    </div>
                    <div className="text-xs text-white/60 italic mb-4">
                      {p.archetype.tagline}
                    </div>
                    <div className="pt-3 border-t border-white/5 space-y-1">
                      {p.top_motives.slice(0, 3).map((m) => {
                        const Icon = getMotiveIcon(m.icon);
                        return (
                          <div key={m.slug} className="flex items-center gap-2 text-sm">
                            <Icon
                              className="w-3.5 h-3.5 flex-shrink-0"
                              style={{ color: m.color }}
                            />
                            <span className="flex-1 truncate text-white/80">{m.name}</span>
                            <span className="font-heading font-bold text-white">
                              {m.score}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
